/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

/** 
  All of the routes for the Material Dashboard 2 React are added here,
  You can add a new route, customize the routes and delete the routes here.

  Once you add a new route on this file it will be visible automatically on
  the Sidenav.

  For adding a new route you can follow the existing routes in the routes array.
  1. The `type` key with the `collapse` value is used for a route.
  2. The `type` key with the `title` value is used for a title inside the Sidenav. 
  3. The `type` key with the `divider` value is used for a divider between Sidenav items.
  4. The `name` key is used for the name of the route on the Sidenav.
  5. The `key` key is used for the key of the route (It will help you with the key prop inside a loop).
  6. The `icon` key is used for the icon of the route on the Sidenav, you have to add a node.
  7. The `collapse` key is used for making a collapsible item on the Sidenav that has other routes
  inside (nested routes), you need to pass the nested routes inside an array as a value for the `collapse` key.
  8. The `route` key is used to store the route location which is used for the react router.
  9. The `href` key is used to store the external links location.
  10. The `title` key is only for the item with the type of `title` and its used for the title text on the Sidenav.
  10. The `component` key is used to store the component of its route.
*/

// Material Dashboard 2 React layouts
import Dashboard from "./layouts/dashboard";
import Dealers from "layouts/tables/dealers";
import Games from "layouts/tables/games";
import PendingAccounts from "layouts/tables/PendingAccounts";
import TimerOff from "layouts/pages/TimeOff";
import Profile from "layouts/profile";
import SignIn from "./layouts/authentication/sign-in";
import SignUp from "./layouts/authentication/sign-up";

// @mui icons
import Icon from "@mui/material/Icon";
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';

import ResetPassword from "layouts/authentication/reset-password/";
import ChangeePassword from "layouts/authentication/change-password/";
import { useSelector } from "react-redux";
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import DealersEdit from "layouts/tables/dealerEdit";
import GamesEdit from "layouts/tables/gamesEdit";
import ShiftGeneration from "layouts/pages/shiftGeneration";
import MovieCreationIcon from '@mui/icons-material/MovieCreation';
import ViewStreamIcon from "@mui/icons-material/ViewStream";
import DealersMistakes from "layouts/pages/dealersMisatkes";
import DealersMistakesEdit from "layouts/pages/dealersMisatkes/DealersMistakesEdit";
import Studios from "layouts/pages/studios";
import Lines from "layouts/pages/lines";
import LinesGeneration from "layouts/pages/linesGeneration/index";
import LinesGenerationEdit from "layouts/pages/linesGeneration/edit";
import LinesEdit from "layouts/pages/lines/linesEdit";
import StudiosEdit from "layouts/pages/studios/studiosEdit";
import ShiftGenerationEdit from "layouts/pages/shiftGeneration/edit";
import DealerRequestHistory from "layouts/pages/dealerRequestHistory";
import ShiftRequestHistory from "layouts/pages/ShiftRequestHistory";
import MistakesHistory from "layouts/pages/mistakesHistory";
import PendingRequests from "layouts/pages/pendingRequests";
import ReviewRequests from "layouts/pages/reviewRequests";
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import RateReviewIcon from "@mui/icons-material/RateReview";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import GavelIcon from "@mui/icons-material/Gavel";
import SchemaIcon from "@mui/icons-material/Schema";

const useData = () => {

  const store = useSelector(store => store.user);
  const role = store?.user?.role ?? 'dealer';

  const rolesConfig = {
    admin: ["dashboard", "profile", "shiftGeneration", "shiftGenerateEdit", "dealers", "games", "gameEdit", 'linesGeneration', 'generationEdit', 'lines', 'lineEdit', 'studios', 'studioEdit', "pendingAccounts", "login", "register", "resetPassword", 'changePassword', 'editDealer', 'pendingRequests', 'dealersMistakes', 'dealersMistakesEdit', 'reviewRequests'],
    manager: ["dashboard", "profile", "shiftGeneration", "shiftGenerateEdit", "dealers", "games", "gameEdit", 'linesGeneration', 'generationEdit', 'lines', 'lineEdit', 'studios', 'studioEdit', "login", "register", "resetPassword", 'changePassword', 'pendingRequests', 'editDealer', 'dealersMistakes', 'dealersMistakesEdit'],
    dealer: ["dashboard",  "profile", "login", "register", "resetPassword", 'changePassword', 'request', 'dealerRequestHistory', 'shiftRequestHistory', 'mistakesHistory'],
    security: ["dashboard", "profile", "login", "register", "resetPassword", 'changePassword'],
    pitbos: ["dashboard", "profile", "login", "register", "resetPassword", 'changePassword', 'dealersMistakes', 'dealersMistakesEdit'],
  };
  const allowedRoutes = rolesConfig[role];
  
  const routes = [
    {
      type: "collapse",
      name: "Dashboard",
      key: "dashboard",
      icon: <Icon fontSize="small">dashboard</Icon>,
      route: "/",
      component: <Dashboard />,
    },
    {
      type: "collapse",
      name: "Dealers",
      key: "dealers",
      icon: <Icon fontSize="small">group</Icon>,
      route: "/dealers",
      component: <Dealers />,
    }, 
    {
      type: "collapse",
      name: "Games",
      key: "games",
      icon: <Icon fontSize="small">casino</Icon>,
      route: "/games",
      component: <Games />,
    },
    {
      type: "",
      name: "Games Edit",
      key: "gameEdit",
      icon: <Icon fontSize="small">casino</Icon>,
      route: "/games/:gameId",
      component: <GamesEdit />,
    },
    {
      type: "collapse",
      name: "Studios",
      key: "studios",
      icon: <MovieCreationIcon />,
      route: "/studios",
      component: <Studios />,
    },
    {
      type: "",
      name: "Studio Edit",
      key: "studioEdit",
      icon: <Icon fontSize="small">casino</Icon>,
      route: "/studios/:studioId",
      component: <StudiosEdit />,
    },
    {
      type: "collapse",
      name: "Lines",
      key: "lines",
      icon: <ViewStreamIcon fontSize="small" />,
      route: "/lines",
      component: <Lines />,
    },
    {
      type: "collapse",
      name: "Lines Generation",
      key: "linesGeneration",
      icon: <SchemaIcon fontSize="small" />,
      route: "/linesGeneration",
      component: <LinesGeneration />,
    },
    {
      type: "",
      name: "Generation Edit",
      key: "generationEdit",
      icon: <Icon fontSize="small">Line</Icon>,
      route: "/linesGeneration/:generationId",
      component: <LinesGenerationEdit />,
    },
    {
      type: "",
      name: "Line Edit",
      key: "lineEdit",
      icon: <Icon fontSize="small">Line</Icon>,
      route: "/lines/:lineId",
      component: <LinesEdit />,
    },
    {
      type: "",
      name: "Edit Dealer",
      key: "editDealer",
      route: "/dealers/:dealerId",
      component: <DealersEdit />,
    },
    {
      type: "collapse",
      name: "Pending Accounts",
      key: "pendingAccounts",
      icon: <PersonAddAlt1Icon fontSize="small" />,
      route: "/pendingAccounts",
      component: <PendingAccounts />,
    },
    {
      type: "collapse",
      name: "Shift generations",
      key: "shiftGeneration",
      icon: <Icon fontSize="small">schedule</Icon>,
      route: "/shiftGeneration",
      component: <ShiftGeneration />,
    },
    {
      type: "",
      name: "Shift Create",
      key: "shiftGenerateEdit",
      route: "/shiftGeneration/:generation",
      component: <ShiftGenerationEdit />,
    },
    {
      type: "collapse",
      name: "Profile",
      key: "profile",
      icon: <Icon fontSize="small">person</Icon>,
      route: "/profile",
      component: <Profile />,
    },
    {
      type: "collapse",
      name: "Request History",
      key: "dealerRequestHistory",
      icon: <CalendarMonthIcon fontSize="small" />,
      route: "/dealerRequestHistory",
      component: <DealerRequestHistory />,
    },
    {
      type: "collapse",
      name: "Shift Requests History",
      key: "shiftRequestHistory",
      icon: <CompareArrowsIcon fontSize="small" />,
      route: "/shiftRequestHistory",
      component: <ShiftRequestHistory />,
    },
    {
      type: "collapse",
      name: "Mistakes History",
      key: "mistakesHistory",
      icon: <GavelIcon fontSize="small" />,
      route: "/mistakesHistory",
      component: <MistakesHistory />,
    },
    
    {
      type: "collapse",
      name: "Pending Requests",
      key: "pendingRequests",
      icon: <PendingActionsIcon fontSize="small" />,
      route: "/pendingRequests",
      component: <PendingRequests />,
    },
    {
      type: "collapse",
      name: "Review Requests",
      key: "reviewRequests",
      icon: <RateReviewIcon fontSize="small" />,
      route: "/reviewRequests",
      component: <ReviewRequests />,
    },
    {
      type: "collapse",
      name: "Dealers Mistakes",
      key: "dealersMistakes",
      icon: <GavelIcon fontSize="small" />,
      route: "/dealersMistakes",
      component: <DealersMistakes />,
    },
    {
      type: "",
      name: "Dealer Mistakes Edit",
      key: "dealersMistakesEdit",
      icon: <Icon fontSize="small">casino</Icon>,
      route: "/dealersMistakes/:mistake",
      component: <DealersMistakesEdit />,
    },
    {
      type: "",
      name: "Request",
      key: "request",
      icon: <Icon fontSize="small">person</Icon>,
      route: "/request",
      component: <TimerOff />,
    },
    {
      type: "",
      name: "login",
      key: "login",
      icon: <Icon fontSize="small">login</Icon>,
      route: "/auth/login",
      component: <SignIn />,
    },
    {
      type: "",
      name: "resetPassword",
      key: "resetPassword",
      icon: <Icon fontSize="small">login</Icon>,
      route: "/auth/reset-password",
      component: <ResetPassword />,
    },
    {
      type: "",
      name: "changePassword",
      key: "changePassword",
      icon: <Icon fontSize="small">login</Icon>,
      route: "/auth/change-password",
      component: <ChangeePassword />,
    },
    {
      type: "",
      name: "Register",
      key: "register",
      icon: <Icon fontSize="small">assignment</Icon>,
      route: "/auth/register",
      component: <SignUp />,
    },
  ].filter((current) => {

    return allowedRoutes.includes(current.key)
  });

  return routes
}
export default useData;
